import type { TrpcRouterOutput } from '@/backend/src/router/trpc/index.js'
import { zUpdateAgentForAdminEndpointInput } from '@/general/src/agent/routes/updateAgentForAdmin/input.js'
import { agentPermissionsOptions, canManageAgents, canViewTxs } from '@/general/src/auth/can.js'
import { toMoney } from '@/general/src/other/money.js'
import { zAddAgentPermanentRecognitionTokensForAdminEndpointInput } from '@/general/src/subscription/routes/addAgentPermanentRecognitionTokensForAdmin/input.js'
import { zAddAgentPermanentScenarioTokensForAdminEndpointInput } from '@/general/src/subscription/routes/addAgentPermanentScenarioTokensForAdmin/input.js'
import { zStopAgentPaidSubscriptionPlanForAdminEndpointInput } from '@/general/src/subscription/routes/stopAgentPaidSubscriptionPlanForAdmin/input.js'
import { getSubscriptionPlanLabel } from '@/general/src/subscription/utils.shared.js'
import { TxsForAdmin } from '@/general/src/subscription/webapp/components/TxsForAdmin/index.js'
import { GeneralLayout } from '@/webapp/src/components/layout/GeneralLayout/index.js'
import { Checkboxesy, Switchy, Textfieldy, useFormy } from '@/webapp/src/lib/formy.js'
import { withPageWrapper } from '@/webapp/src/lib/pageWrapper.js'
import { adminAgentEditRoute } from '@/webapp/src/lib/routes.js'
import { trpc } from '@/webapp/src/lib/trpc.js'
import {
  Block,
  Button,
  Buttons,
  FormItems,
  LabeledValues,
  Segment,
  Segments,
} from '@/webapp/src/lib/uninty.components.js'
import { useRouteParams } from '@/webapp/src/lib/useRoute.js'
import { formatDate } from 'date-fns'

const AddPermanentScenarioTokens = ({ agent }: { agent: TrpcRouterOutput['getAgentForAdmin']['agent'] }) => {
  const addAgentPermanentScenarioTokensForAdmin = trpc.addAgentPermanentScenarioTokensForAdmin.useMutation()
  const trpcUtils = trpc.useUtils()
  const formy = useFormy({
    initialValues: {
      agentId: agent.id,
      tokensCount: 100,
    },
    validationSchema: zAddAgentPermanentScenarioTokensForAdminEndpointInput,
    onSubmit: async ({ valuesInput }) => {
      await addAgentPermanentScenarioTokensForAdmin.mutateAsync(valuesInput)
      await trpcUtils.getAgentForAdmin.refetch({
        agentSn: agent.sn,
      })
    },
    successMessage: 'Несгораемые токены сценариев начислены',
  })
  return (
    <Segment title="Начисление несгораемых токенов сценариев">
      <FormItems as="form" {...formy.formProps}>
        <Textfieldy label="Количество токенов" {...formy.getFieldProps('tokensCount')} />
        <Buttons>
          <Button {...formy.buttonProps} type="submit">
            Начислить
          </Button>
        </Buttons>
      </FormItems>
    </Segment>
  )
}

const AddPermanentRecognitionTokens = ({ agent }: { agent: TrpcRouterOutput['getAgentForAdmin']['agent'] }) => {
  const addAgentPermanentRecognitionTokensForAdmin = trpc.addAgentPermanentRecognitionTokensForAdmin.useMutation()
  const trpcUtils = trpc.useUtils()
  const formy = useFormy({
    initialValues: {
      agentId: agent.id,
      tokensCount: 100,
    },
    validationSchema: zAddAgentPermanentRecognitionTokensForAdminEndpointInput,
    onSubmit: async ({ valuesInput }) => {
      await addAgentPermanentRecognitionTokensForAdmin.mutateAsync(valuesInput)
      await trpcUtils.getAgentForAdmin.refetch({
        agentSn: agent.sn,
      })
    },
    successMessage: 'Несгораемые токены распознавания начислены',
  })
  return (
    <Segment title="Начисление несгораемых токенов распознования">
      <FormItems as="form" {...formy.formProps}>
        <Textfieldy label="Количество токенов" {...formy.getFieldProps('tokensCount')} />
        <Buttons>
          <Button {...formy.buttonProps} type="submit">
            Начислить
          </Button>
        </Buttons>
      </FormItems>
    </Segment>
  )
}

const StopSubscription = ({ agent }: { agent: TrpcRouterOutput['getAgentForAdmin']['agent'] }) => {
  const stopAgentPaidSubscriptionPlanForAdmin = trpc.stopAgentPaidSubscriptionPlanForAdmin.useMutation()
  const trpcUtils = trpc.useUtils()
  const formy = useFormy({
    initialValues: {
      agentId: agent.id,
    },
    validationSchema: zStopAgentPaidSubscriptionPlanForAdminEndpointInput,
    onSubmit: async ({ valuesInput }) => {
      await stopAgentPaidSubscriptionPlanForAdmin.mutateAsync(valuesInput)
      await trpcUtils.getAgentForAdmin.refetch({
        agentSn: agent.sn,
      })
    },
    successMessage: 'Продление подписки отключено',
  })
  return (
    <FormItems as="form" {...formy.formProps}>
      <Buttons>
        <Button {...formy.buttonProps} type="submit">
          Отключить продление подписки
        </Button>
      </Buttons>
    </FormItems>
  )
}

export const AdminAgentEditPage = withPageWrapper({
  title: 'Редактирование агента',
  Layout: GeneralLayout,
  authorizedAdminsOnly: true,
  useQuery: () => {
    const { routeParams } = useRouteParams(adminAgentEditRoute)
    return trpc.getAgentForAdmin.useQuery({
      agentSn: +routeParams.agentSn,
    })
  },
  showLoaderOnFetching: false,
  checkAccess: ({ ctx }) => canManageAgents(ctx.me.admin),
  setProps: ({ queryResult, getAuthorizedAdminMe }) => ({
    agent: queryResult.data.agent,
    me: getAuthorizedAdminMe(),
  }),
})(({ agent, me }) => {
  const updateAgentForAdmin = trpc.updateAgentForAdmin.useMutation()
  const trpcUtils = trpc.useUtils()
  const formy = useFormy({
    initialValues: {
      agentId: agent.id,
      name: agent.name || '',
      email: agent.email,
      phone: agent.phone || '',
      permissions: agent.permissions,
      banned: !!agent.bannedAt,
      organizationSn: agent.organization?.sn || '',
      referrerAgentSn: agent.referrerAgent?.sn || '',
      referralBonusShare: agent.referralBonusShare || ('0.1' as never as number),
    },
    validationSchema: zUpdateAgentForAdminEndpointInput,
    onSubmit: async ({ valuesInput }) => {
      const res = await updateAgentForAdmin.mutateAsync({
        ...valuesInput,
      })
      trpcUtils.getAgentForAdmin.setData({ agentSn: res.agent.sn }, res)
    },
    successMessage: 'Агент обновлен',
  })
  return (
    <Block fcnw>
      <Segments>
        <Segment title={`Агент #${agent.sn}`} size="m">
          <LabeledValues
            items={[
              ['SN', agent.sn],
              ['Имя', agent.name],
              ['Телефон', agent.phone],
              ['Почта', agent.email],
              ['Организация', agent.organizationLabel],
              ['Номер реферера', agent.referrerAgent?.sn || '—'],
              ['Доля реферальной программы', agent.referralBonusShare],
              ['Баланс реферальной программы', toMoney({ amount: agent.referrerBalance })],
              ['Тарифный план', getSubscriptionPlanLabel(agent.subscriptionPlan)],
              [
                'Дата истечения платного тарифного плана',
                agent.subscriptionPlan !== 'free' && agent.paidSubscriptionPlanExpiresAt
                  ? formatDate(agent.paidSubscriptionPlanExpiresAt, 'dd.MM.yyyy HH:mm')
                  : '—',
              ],
              [
                'Дата продления платного тарифного плана',
                agent.subscriptionPlan !== 'free' && agent.nextPaidSubscriptionPlanRefreshTryWillAt
                  ? formatDate(agent.nextPaidSubscriptionPlanRefreshTryWillAt, 'dd.MM.yyyy HH:mm')
                  : '—',
              ],
              [
                'Агент собирается продлевать платный тарифный план',
                agent.subscriptionPlan !== 'free' ? agent.shouldRefreshPaidSubscriptionPlan : '—',
              ],
              [
                'Дата продления бесплатного тарифного плана',
                agent.subscriptionPlan === 'free' && agent.freeSubscriptionPlanWillRefreshAt
                  ? formatDate(agent.freeSubscriptionPlanWillRefreshAt, 'dd.MM.yyyy HH:mm')
                  : '—',
              ],
              [
                'Количество неудачных попыток продления платного тарифного плана',
                agent.paidSubscriptionPlanRefreshFailedTriesCount,
              ],
              [
                'Последняя ошибка продления платного тарифного плана',
                agent.lastPaidSubscriptionPlanRefreshFailedTryErrorMessage,
              ],
              ['Несгораемых токенов распознавания документов', agent.permanentRecognitionTokensCount],
              ['Несгораемых токенов для сценариев', agent.permanentScenarioTokensCount],
              ['Сгораемых токенов распознавания документов', agent.temporaryRecognitionTokensCount],
              ['Сгораемых токенов для сценариев', agent.temporaryScenarioTokensCount],
            ]}
          />
        </Segment>
        <Segment title={`Редактирование`} size="m">
          <FormItems as="form" {...formy.formProps}>
            <Textfieldy
              label="Организация"
              {...formy.getFieldProps('organizationSn')}
              disabled
              hint={`${agent.organizationLabel} (Невозможно изменить)`}
            />
            <Textfieldy label="Номер реферера" {...formy.getFieldProps('referrerAgentSn')} />
            <Textfieldy label="Доля реферальной программы" {...formy.getFieldProps('referralBonusShare')} />
            <Textfieldy label="Имя" {...formy.getFieldProps('name')} />
            <Textfieldy label="Телефон" {...formy.getFieldProps('phone')} />
            <Textfieldy label="Почта" {...formy.getFieldProps('email')} />
            <Checkboxesy label="Права" options={agentPermissionsOptions} {...formy.getFieldProps('permissions')} />
            <Switchy {...formy.getFieldProps('banned')} label="Заблокирован" optionLabel="Да" />
            <Buttons>
              <Button {...formy.buttonProps} type="submit">
                Сохранить
              </Button>
            </Buttons>
          </FormItems>
        </Segment>
        <Segment title={`Управление подпиской`} size="m">
          {agent.subscriptionPossibleActions.stopPaidSubscription.can ? (
            <StopSubscription agent={agent} />
          ) : (
            <p>Мы умеем сейчас через админку только отключать продление подписки. Но у него оно и так не включено</p>
          )}
        </Segment>
        <AddPermanentScenarioTokens agent={agent} />
        <AddPermanentRecognitionTokens agent={agent} />
        {canViewTxs(me.admin) && (
          <Segment title={`Транзакции`} size="m">
            <TxsForAdmin agentId={agent.id} />
          </Segment>
        )}
      </Segments>
    </Block>
  )
})
